/**
 * Every person's avatar renders through these components: PersonAvatar, which falls back to the
 * person's initials, and CareMapMemberAvatar, which falls back to an illustration. Both draw a photo,
 * then a chosen illustration, then a colour, by the rules in AvatarPresentation, so a person looks
 * the same wherever they appear. The care map nodes keep their own look but use the same rules.
 * To agency staff either draws only the photo, else the initials on their colour (AgencyAvatarLook),
 * unless `showsStoredAvatar` asks for the avatar as stored.
 *
 * A care map photo we host is read only through its audited link: one that arrives without it shows
 * the failed figure and is reported. An app user's own picture, which has no audited route, and a
 * file this device has just uploaded are read as stored.
 */
import { FC, ReactNode } from 'react';
import { IonAvatar } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { EditIcon2, UserIcon } from '../theme/Icons';
import { useAvatarImage } from './useAvatarImage';
import { AvatarPresentation } from './AvatarPresentation';
import { AgencyAvatarLook } from './AgencyAvatarLook';
import { IAvatarIdentity } from './IAvatarIdentity';

interface IAvatarFrameProps {
    small?: boolean;
    xsmall?: boolean;
    // explicit pixel diameter; overrides the small/xsmall presets when set
    size?: number;
    onClick?: (e: React.MouseEvent) => void;
}

// Foreground for the failed-image figure, which sits on the neutral fill.
const PLACEHOLDER_TINT = 'var(--ion-color-step-350)';

const AvatarPlaceholder: FC<{ color: string; dim: number }> = ({ color, dim }) => (
    <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color
    }}>
        <UserIcon color="inherit" customSize={dim * 0.55} />
    </div>
);

// Masked because initials come from a name, and session replay records unmasked text.
const AvatarInitials: FC<{ initials: string; color: string; dim: number }> = ({ initials, color, dim }) => (
    <div className="mp-mask" style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        fontSize: dim * 0.4,
        fontWeight: 500,
        lineHeight: 1
    }}>
        {initials}
    </div>
);

const AvatarImage: FC<{ url: string }> = ({ url }) => (
    <img
        src={url}
        alt=""
        style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // TODO: better way to set these up / scale / position them, based on our
            // "profile image" effort in the future
            objectPosition: '75% 40%'
        }}
    />
);

const EditableAvatarFrame: FC<{ onClick: (e: React.MouseEvent) => void; children: ReactNode }> = ({ onClick, children }) => (
    <div style={{ cursor: 'pointer', position: 'relative'}} onClick={onClick}>
        {children}
        <div style={{ position: 'absolute', bottom: -2, right: -2 }}>
            <EditIcon2 size="small" shape="round" backgroundColor="white" borderColor="medium" />
        </div>
    </div>
);

// Renders what AvatarPresentation describes. An image still coming after the visible timeout shows
// the same placeholder as one that failed.
const AvatarCore: FC<IAvatarFrameProps & {
    identity: IAvatarIdentity | undefined;
    name: string;
    fallsBackToIllustration: boolean;
    showsStoredAvatar?: boolean;
}> = ({ identity, name, fallsBackToIllustration, showsStoredAvatar = false, small = false, xsmall = false, size: sizeOverride, onClick }) => {
    const presentation = useInstance(AvatarPresentation);
    const agencyLook = useInstance(AgencyAvatarLook);
    useSubscription(() => agencyLook);

    const identifiesByPhotoOrInitials = !showsStoredAvatar && agencyLook.identifiesByPhotoOrInitials();
    const description = presentation.describeAvatar({ identity, name, fallsBackToIllustration, identifiesByPhotoOrInitials });

    const { url, status } = useAvatarImage({
        photo: identity?.photo,
        photoHref: identity?.links?.photo?.href,
        photoHasNoAuditedRoute: identity?.photoHasNoAuditedRoute,
        illustration: description.illustration
    });

    const dim = sizeOverride ?? (xsmall ? 32 : small ? 42 : 64);

    // A loaded image paints its own background. The illustrations' washes are translucent and drawn
    // to sit on white, so the neutral fill behind them would grey them.
    const imageLoaded = !!url && status === 'loaded';

    const avatar = (
        <IonAvatar
            style={{ width: dim, height: dim, flexShrink: 0, backgroundColor: imageLoaded ? undefined : description.background, overflow: 'hidden' }}
            data-avatar={url ? description.imageVisual : description.fallbackVisual}
            data-avatar-status={description.hasImage ? status : 'none'}
        >
            {url && imageLoaded && <AvatarImage url={url} />}

            {description.hasImage && (status === 'slow' || status === 'failed') && <AvatarPlaceholder color={PLACEHOLDER_TINT} dim={dim} />}

            {description.showsIdentity && description.initials && (
                <AvatarInitials initials={description.initials} color={description.foreground} dim={dim} />
            )}

            {description.showsIdentity && !description.initials && (
                <AvatarPlaceholder color={description.foreground} dim={dim} />
            )}
        </IonAvatar>
    );

    return onClick ? <EditableAvatarFrame onClick={onClick}>{avatar}</EditableAvatarFrame> : avatar;
};

// `showsStoredAvatar` draws the avatar as stored even to agency staff: for an avatar picker or an
// edit form, and for a care team member, whom staff know by the look the family gave them.
interface IStoredAvatarProps {
    showsStoredAvatar?: boolean;
}

// A person, who without a photo or chosen illustration shows their initials on their colour.
export const PersonAvatar: FC<IAvatarFrameProps & IStoredAvatarProps & {
    identity: IAvatarIdentity | undefined;
    name: string;
}> = props => (
    <AvatarCore {...props} fallsBackToIllustration={false} />
);

// A care map member, who without a photo or colour shows an illustration. Staff see `name`'s
// initials instead, so it is needed unless the stored avatar is shown.
type ICareMapMemberAvatarProps = IAvatarFrameProps & { identity: IAvatarIdentity | undefined; } & (
    | { showsStoredAvatar: true; name?: undefined; }
    | { showsStoredAvatar?: false; name: string; }
);

export const CareMapMemberAvatar: FC<ICareMapMemberAvatarProps> = ({ name = '', ...props }) => (
    <AvatarCore {...props} name={name} fallsBackToIllustration />
);
